import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Save, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContentEditor = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (sectionKey, newContent) => {
    setContent(prev =>
      prev.map(item =>
        item.section_key === sectionKey
          ? { ...item, content: newContent }
          : item
      )
    );
  };

  const handleSave = async (sectionKey) => {
    setSaving(prev => ({ ...prev, [sectionKey]: true }));
    
    try {
      const section = content.find(item => item.section_key === sectionKey);
      await axios.put(`${BACKEND_URL}/api/content/${sectionKey}`, {
        content: section.content
      });
      toast.success('Content updated successfully');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  const groupByPage = (sections) => {
    const grouped = {};
    sections.forEach(section => {
      const page = section.section_key.split('_')[0];
      if (!grouped[page]) grouped[page] = [];
      grouped[page].push(section);
    });
    return grouped;
  };

  const parseArrayContent = (content) => {
    try {
      const parsed = JSON.parse(content);
      return Array.isArray(parsed) ? parsed.join('\n') : content;
    } catch {
      return content;
    }
  };

  const stringifyArrayContent = (content) => {
    const lines = content.split('\n').filter(line => line.trim() !== '');
    return JSON.stringify(lines);
  };

  const isArrayContent = (sectionKey) => {
    return sectionKey.includes('rules_');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="animate-spin text-brand-purple" size={48} />
      </div>
    );
  }

  const groupedContent = groupByPage(content);

  return (
    <div className="space-y-6">
      <Tabs defaultValue="home" className="w-full">
        <TabsList className="grid w-full grid-cols-7 mb-6">
          <TabsTrigger value="home" className="font-body">Home</TabsTrigger>
          <TabsTrigger value="feature" className="font-body">Features</TabsTrigger>
          <TabsTrigger value="about" className="font-body">About</TabsTrigger>
          <TabsTrigger value="value" className="font-body">Values</TabsTrigger>
          <TabsTrigger value="rules" className="font-body">Rules</TabsTrigger>
          <TabsTrigger value="contact" className="font-body">Contact</TabsTrigger>
          <TabsTrigger value="footer" className="font-body">Footer</TabsTrigger>
        </TabsList>

        {Object.keys(groupedContent).map(page => (
          <TabsContent key={page} value={page} className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-6">
                {page.charAt(0).toUpperCase() + page.slice(1)} Page Content
              </h2>
              
              {groupedContent[page].map((section) => (
                <div
                  key={section.section_key}
                  className="bg-white rounded-xl border border-gray-200 p-6 mb-4"
                  data-testid={`content-section-${section.section_key}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <Label className="text-lg font-semibold text-gray-900 font-heading">
                        {section.title}
                      </Label>
                      <p className="text-sm text-gray-500 font-body mt-1">
                        Key: {section.section_key}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleSave(section.section_key)}
                      disabled={saving[section.section_key]}
                      className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
                      data-testid={`save-${section.section_key}`}
                    >
                      {saving[section.section_key] ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>

                  {isArrayContent(section.section_key) ? (
                    <Textarea
                      value={parseArrayContent(section.content)}
                      onChange={(e) =>
                        handleContentChange(
                          section.section_key,
                          stringifyArrayContent(e.target.value)
                        )
                      }
                      rows={10}
                      className="font-body"
                      placeholder="Enter each item on a new line"
                    />
                  ) : section.content.length > 100 ? (
                    <Textarea
                      value={section.content}
                      onChange={(e) =>
                        handleContentChange(section.section_key, e.target.value)
                      }
                      rows={4}
                      className="font-body"
                    />
                  ) : (
                    <Input
                      value={section.content}
                      onChange={(e) =>
                        handleContentChange(section.section_key, e.target.value)
                      }
                      className="font-body"
                    />
                  )}
                </div>
              ))}
            </motion.div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
